import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ScrollText, ShieldCheck, Calendar } from "lucide-react";

const RESPONSABLE = "Daniela Muñoz Jiménez";
const SITIO = "https://siempresalud.mx";
const CORREO_LEGAL = "legal@siempresalud.mx";
const CORREO_PRIVACIDAD = "privacidad@siempresalud.mx";
const FECHA_ACTUALIZACION = "10 de febrero de 2026";

const SECCIONES = [
  { id: "definiciones", label: "1. Definiciones" },
  { id: "naturaleza", label: "2. Naturaleza del servicio" },
  { id: "no-medico", label: "3. Lo que NO es Siempre Salud" },
  { id: "cuentas", label: "4. Cuentas de usuario" },
  { id: "obligaciones-medico", label: "5. Obligaciones del especialista" },
  { id: "precios", label: "6. Precios y pagos" },
  { id: "reembolsos", label: "7. Reembolsos y cancelaciones" },
  { id: "conducta", label: "8. Conducta prohibida" },
  { id: "propiedad", label: "9. Propiedad intelectual" },
  { id: "responsabilidad", label: "10. Limitación de responsabilidad" },
  { id: "modificaciones", label: "11. Modificaciones al servicio" },
  { id: "vigencia", label: "12. Vigencia y terminación" },
  { id: "ley", label: "13. Legislación aplicable y jurisdicción" },
  { id: "contacto", label: "14. Contacto" },
];

export default function TerminosCondiciones() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="page-terminos">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">
        <LegalHero
          eyebrow="Aviso legal"
          title="Términos y Condiciones"
          subtitle="Al usar Siempre Salud aceptas los siguientes términos, que rigen tu relación contractual con el Responsable."
          fecha={FECHA_ACTUALIZACION}
          icon={<ScrollText size={22} />}
        />

        <TocIndex />

        <ImportantBanner />

        <article className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8 space-y-8 text-sm text-foreground leading-relaxed">
          <Sec id="definiciones" title="1. Definiciones">
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Responsable:</b> {RESPONSABLE}, persona física titular del sitio {SITIO}.</li>
              <li><b>Plataforma:</b> el sitio web, aplicación móvil y servicios asociados operados bajo la marca &ldquo;Siempre Salud&rdquo;.</li>
              <li><b>Persona usuaria:</b> persona mayor de edad que utiliza el servicio de orientación clínica con IA.</li>
              <li><b>Especialista o Médico:</b> profesional de la salud que publica su ficha en el Directorio.</li>
              <li><b>Consulta IA:</b> respuesta generada por un modelo de inteligencia artificial (Gemini) a partir de la historia clínica y los síntomas capturados por la Persona usuaria.</li>
              <li><b>OTC:</b> medicamentos de venta libre autorizados en México (Fracciones V y VI de COFEPRIS).</li>
            </ul>
          </Sec>

          <Sec id="naturaleza" title="2. Naturaleza del servicio">
            <p>
              Siempre Salud es una plataforma de <b>orientación de salud generada por
              inteligencia artificial</b>, basada exclusivamente en <b>Guías de Práctica
              Clínica (GPC) de México</b> (IMSS, Secretaría de Salud, SSA) y limitada a
              recomendaciones de medicamentos <b>de venta libre (OTC)</b>.
            </p>
            <p>
              La plataforma también ofrece un <b>Directorio de Especialistas</b> donde
              médicos con perfil verificado pueden ser contactados directamente por personas usuarias
              que la propia IA derive cuando el cuadro exceda su alcance seguro.
            </p>
          </Sec>

          <Sec id="no-medico" title="3. Lo que NO es Siempre Salud">
            <ul className="list-disc pl-5 space-y-1">
              <li><b>NO es una consulta médica</b> en los términos del artículo 78 y demás aplicables de la Ley General de Salud.</li>
              <li><b>NO emite diagnósticos, recetas ni indicaciones de medicamentos que requieran receta médica</b> (Fracciones I a IV de COFEPRIS).</li>
              <li><b>NO establece una relación médico-paciente</b> entre la persona usuaria y el Responsable.</li>
              <li><b>NO sustituye la valoración presencial</b> por un profesional de la salud.</li>
              <li><b>NO atiende emergencias médicas.</b> En caso de urgencia (dolor en el pecho, dificultad respiratoria grave, sangrado activo, pérdida de conciencia, síntomas neurológicos súbitos, sospecha de infarto o EVC), <b>llama al 911</b> o acude al servicio de urgencias más cercano de inmediato.</li>
            </ul>
            <p>
              Al usar el servicio reconoces y aceptas estos alcances y limitaciones.
            </p>
          </Sec>

          <Sec id="cuentas" title="4. Cuentas de usuario">
            <ul className="list-disc pl-5 space-y-1">
              <li>Al crear una cuenta manifiestas que eres <b>mayor de edad</b> (18 años o más) o que actúas con supervisión de quien ejerza la patria potestad o tutela.</li>
              <li>La información que proporciones (nombre, correo, datos clínicos, documentos profesionales) debe ser <b>veraz, exacta y actualizada</b>. Eres responsable de mantener actualizada tu información.</li>
              <li>Eres responsable del uso de tu contraseña y de toda la actividad que ocurra bajo tu cuenta. Debes notificar de inmediato cualquier uso no autorizado.</li>
              <li>El Responsable se reserva el derecho de suspender o cancelar cuentas cuando existan indicios de suplantación, uso indebido, violación de estos Términos o incumplimiento de obligaciones legales.</li>
            </ul>
          </Sec>

          <Sec id="obligaciones-medico" title="5. Obligaciones del especialista">
            <p>Los médicos que publiquen su ficha en el Directorio aceptan expresamente:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Que sus datos profesionales son <b>veraces y vigentes</b>: cédula profesional emitida por la SEP/DGP, cédula de especialidad cuando aplique, y aviso de funcionamiento COFEPRIS del consultorio.</li>
              <li>Autorizar la publicación de nombre, especialidad, ubicación y medios de contacto en el Directorio público.</li>
              <li>Ser el <b>único responsable</b> de los servicios profesionales que preste a las personas usuarias que los contacten a través del Directorio. Siempre Salud no participa, no supervisa ni garantiza la calidad de dichas consultas presenciales o remotas.</li>
              <li>Cumplir con la normativa aplicable en materia de ejercicio profesional (Ley General de Salud, NOM-004-SSA3-2012 del Expediente Clínico, LFPDPPP en el manejo de datos personales de las personas a las que atienda).</li>
              <li>Que el Responsable puede <b>suspender u ocultar la ficha</b> del Directorio cuando existan indicios razonables de datos falsos, cédulas inválidas o quejas fundadas.</li>
            </ul>
          </Sec>

          <Sec id="precios" title="6. Precios y pagos">
            <table className="w-full text-sm border border-border rounded-md overflow-hidden">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Producto</th>
                  <th className="text-left px-3 py-2 font-semibold">Precio</th>
                  <th className="text-left px-3 py-2 font-semibold">Modalidad</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="px-3 py-2">Consulta IA</td>
                  <td className="px-3 py-2 font-semibold">$39.00 MXN</td>
                  <td className="px-3 py-2">Pago único por consulta</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3 py-2">Plan Especialista mensual</td>
                  <td className="px-3 py-2 font-semibold">$69.00 MXN / mes</td>
                  <td className="px-3 py-2">Suscripción con renovación automática</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3 py-2">Plan Especialista anual</td>
                  <td className="px-3 py-2 font-semibold">$699.00 MXN / año</td>
                  <td className="px-3 py-2">Suscripción con renovación automática (ahorro vs mensual)</td>
                </tr>
              </tbody>
            </table>
            <ul className="list-disc pl-5 space-y-1">
              <li>Todos los precios están expresados en pesos mexicanos (MXN) e incluyen los impuestos aplicables al consumidor final.</li>
              <li>Los pagos son procesados por <b>Stripe Payments</b>. Siempre Salud NO almacena el número completo de tarjeta ni el código de verificación (CVV).</li>
              <li>El Responsable podrá modificar los precios en cualquier momento. Los cambios se aplicarán a nuevas compras y a renovaciones posteriores; nunca de forma retroactiva a un pago ya realizado.</li>
            </ul>
          </Sec>

          <Sec id="reembolsos" title="7. Reembolsos y cancelaciones">
            <h3 className="font-semibold text-[#0F2942] mt-2">Consulta IA ($39 MXN)</h3>
            <p>
              La consulta IA es un servicio digital de ejecución inmediata. Podrás solicitar
              un reembolso, <b>a criterio del Responsable</b>, únicamente cuando el servicio
              haya fallado por causas imputables a la plataforma (por ejemplo, cobro sin
              recomendación entregada). Solicita el reembolso al correo{" "}
              <a className="link" href={`mailto:${CORREO_LEGAL}`}>{CORREO_LEGAL}</a>{" "}
              dentro de los <b>7 días</b> posteriores al cargo, indicando el identificador de
              la operación.
            </p>

            <h3 className="font-semibold text-[#0F2942] mt-3">Suscripción de especialistas</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Puedes cancelar tu suscripción en cualquier momento desde tu panel de médico.</li>
              <li>La cancelación surte efecto <b>al finalizar el periodo pagado en curso</b>. Durante ese periodo tu ficha permanece visible.</li>
              <li>No se realizan reembolsos por periodos parciales ya iniciados, salvo obligación legal en contrario.</li>
            </ul>
          </Sec>

          <Sec id="conducta" title="8. Conducta prohibida">
            <p>Está expresamente prohibido, entre otras conductas:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Utilizar la plataforma para obtener recetas o indicaciones de medicamentos que requieran prescripción médica.</li>
              <li>Suplantar la identidad de otra persona o falsear datos profesionales.</li>
              <li>Cargar contenido ilícito, difamatorio, discriminatorio o que infrinja derechos de terceros.</li>
              <li>Intentar vulnerar los mecanismos de seguridad, realizar ingeniería inversa o extraer datos de forma masiva.</li>
              <li>Usar la plataforma para actividades comerciales distintas a las expresamente autorizadas (por ejemplo, spam a médicos del Directorio).</li>
            </ul>
          </Sec>

          <Sec id="propiedad" title="9. Propiedad intelectual">
            <p>
              Los derechos de autor, marcas, código fuente, diseños, textos y demás elementos
              del sitio son propiedad del Responsable o de sus licenciantes y están
              protegidos por la Ley Federal del Derecho de Autor y la Ley Federal de
              Protección a la Propiedad Industrial. Se te otorga una licencia limitada, no
              exclusiva, no transferible y revocable para acceder y usar el servicio conforme
              a estos Términos. Cualquier uso distinto requiere autorización previa por
              escrito del Responsable.
            </p>
          </Sec>

          <Sec id="responsabilidad" title="10. Limitación de responsabilidad">
            <p>
              En la máxima medida permitida por la ley aplicable, el Responsable no será
              responsable por daños indirectos, incidentales, consecuenciales o punitivos
              derivados del uso o imposibilidad de uso del servicio, incluyendo, sin
              limitación:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Decisiones clínicas que el Usuario tome basadas en la orientación de la IA sin acudir a un profesional de la salud.</li>
              <li>Servicios profesionales que un especialista del Directorio preste directamente a la persona usuaria.</li>
              <li>Fallas atribuibles a terceros (procesadores de pago, proveedores de IA, proveedores de correo, redes de telecomunicaciones).</li>
              <li>Uso de la plataforma en violación a los presentes Términos.</li>
            </ul>
            <p>
              La responsabilidad total del Responsable frente a cualquier reclamación se
              limita al monto que el Usuario haya pagado a Siempre Salud en los seis meses
              anteriores al evento que da origen a la reclamación.
            </p>
          </Sec>

          <Sec id="modificaciones" title="11. Modificaciones al servicio y a estos Términos">
            <p>
              El Responsable podrá modificar los presentes Términos y las características
              del servicio en cualquier momento. Los cambios sustanciales serán notificados a
              través del sitio o por correo electrónico con una antelación razonable. El uso
              continuado del servicio después de la notificación constituye la aceptación de
              los nuevos Términos.
            </p>
          </Sec>

          <Sec id="vigencia" title="12. Vigencia y terminación">
            <p>
              Estos Términos permanecerán vigentes mientras utilices el servicio. El
              Responsable podrá terminar la relación en cualquier momento por incumplimiento
              de estos Términos, por sospecha razonable de uso fraudulento o por
              requerimiento legal.
            </p>
          </Sec>

          <Sec id="ley" title="13. Legislación aplicable y jurisdicción">
            <p>
              Estos Términos se rigen por las leyes federales de los <b>Estados Unidos
              Mexicanos</b>. Para la interpretación y cumplimiento de los mismos, las partes
              se someten expresamente a la jurisdicción de los tribunales competentes de la{" "}
              <b>Ciudad de México</b>, renunciando a cualquier otro fuero que pudiera
              corresponderles por razón de su domicilio presente o futuro.
            </p>
          </Sec>

          <Sec id="contacto" title="14. Contacto">
            <p>
              Para cualquier consulta sobre estos Términos escríbenos a{" "}
              <a className="link" href={`mailto:${CORREO_LEGAL}`}>{CORREO_LEGAL}</a>. Para
              asuntos relacionados con datos personales, consulta el{" "}
              <Link to="/aviso-de-privacidad" className="link">Aviso de Privacidad</Link>{" "}
              o escribe a{" "}
              <a className="link" href={`mailto:${CORREO_PRIVACIDAD}`}>{CORREO_PRIVACIDAD}</a>.
            </p>
          </Sec>

          <div className="pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
            <div>
              <Calendar size={12} className="inline mr-1" />
              Última actualización: <b>{FECHA_ACTUALIZACION}</b>
            </div>
            <div>Responsable: {RESPONSABLE}</div>
          </div>
        </article>

        <div className="mt-6 flex items-center justify-between">
          <Link to="/" data-testid="terminos-back-btn">
            <Button variant="outline" size="sm">
              <ArrowLeft size={14} /> Volver al inicio
            </Button>
          </Link>
          <Link to="/aviso-de-privacidad" data-testid="terminos-to-aviso-link">
            <Button variant="ghost" size="sm" className="text-[#2563EB]">
              <ShieldCheck size={14} /> Aviso de Privacidad
            </Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ImportantBanner() {
  return (
    <div
      className="mt-6 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm leading-relaxed"
      data-testid="terminos-emergencia-banner"
    >
      <b>En caso de emergencia médica llama al 911</b> o acude al servicio de urgencias más
      cercano. Siempre Salud NO atiende urgencias ni sustituye la valoración de un
      profesional de la salud.
    </div>
  );
}

function LegalHero({ eyebrow, title, subtitle, fecha, icon }) {
  return (
    <section className="rounded-xl border border-border bg-white p-6 sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-2xl">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
            {eyebrow}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#0F2942] tracking-tight mt-1">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{subtitle}</p>
          )}
          <div className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <Calendar size={12} /> Última actualización: <b>{fecha}</b>
          </div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-[#0F2942]/5 text-[#0F2942] grid place-items-center shrink-0">
          {icon}
        </div>
      </div>
    </section>
  );
}

function TocIndex() {
  return (
    <nav className="mt-6 rounded-xl border border-border bg-white p-5" aria-label="Índice del documento" data-testid="terminos-toc">
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
        Contenido
      </div>
      <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {SECCIONES.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="text-[#0F2942] hover:text-[#2563EB] hover:underline decoration-dotted underline-offset-4"
            >
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Sec({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`terminos-sec-${id}`}>
      <h2 className="font-display text-lg sm:text-xl font-semibold text-[#0F2942] tracking-tight">
        {title}
      </h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}
